import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { api, ApiError, type Me } from '../api.ts';
import { TokenReveal } from '../components/DeviceHealth.tsx';

interface DeviceRow {
  id: string; name: string; serial: string; clusterLabel: string;
  status: 'online' | 'offline' | 'stale' | 'never_connected'; lastSeenAt: string | null;
  firmwareVersion?: string | null; rssi?: number | null; activeSet?: number | null; sync?: 'synced' | 'pending' | 'unknown'; errorCount?: number;
}
interface Created { id: string; name: string; serial: string; token: string }
interface Paired { id: string; name: string; serial: string; repaired: boolean }

/** The unit shows a 6-digit code; entering it assigns the unit to this organization (no token to copy). */
function PairDevice({ onPaired, onCancel }: { onPaired: (d: Paired) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onPaired(await api<Paired>('/api/devices/pair', { method: 'POST', body: { code, ...(name.trim() ? { name } : {}) } }));
    } catch (err) {
      setError(err instanceof ApiError ? t(`deviceAdmin.errors.${err.status === 429 ? 'rate_limited' : err.code}`, { defaultValue: `${t('common.error')} (${err.code})` }) : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit} aria-labelledby="pair-dev-title">
      <h2 id="pair-dev-title">{t('deviceAdmin.pairTitle')}</h2>
      <p className="faint">{t('deviceAdmin.pairHint')}</p>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="pd-code">{t('deviceAdmin.pairCode')}</label>
          <input id="pd-code" value={code} required inputMode="numeric" autoComplete="one-time-code" pattern="\d{3}[\s\-]?\d{3}" maxLength={7} placeholder="123 456" onChange={(e) => setCode(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="pd-name">{t('deviceAdmin.name')}</label>
          <input id="pd-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder={t('deviceAdmin.namePlaceholder')} />
        </div>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="row">
        <button className="btn" disabled={busy}>{busy ? t('editor.saving') : t('deviceAdmin.pairCreate')}</button>
        <button type="button" className="btn btn--ghost" onClick={onCancel}>{t('connections.cancel')}</button>
      </div>
    </form>
  );
}

function AddDevice({ onCreated, onCancel }: { onCreated: (d: Created) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const models = useQuery({ queryKey: ['cluster-models'], queryFn: () => api<{ id: string; label: string }[]>('/api/cluster-models') });
  const [name, setName] = useState('');
  const [serial, setSerial] = useState('');
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated(await api<Created>('/api/devices', { method: 'POST', body: { name, serial, clusterModelId: model || models.data?.[0]?.id } }));
    } catch (err) {
      setError(err instanceof ApiError ? t(`deviceAdmin.errors.${err.code}`, { defaultValue: `${t('common.error')} (${err.code})` }) : t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit} aria-labelledby="add-dev-title">
      <h2 id="add-dev-title">{t('deviceAdmin.addTitle')}</h2>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="nd-name">{t('deviceAdmin.name')}</label>
          <input id="nd-name" value={name} maxLength={60} required onChange={(e) => setName(e.target.value)} placeholder={t('deviceAdmin.namePlaceholder')} />
        </div>
        <div className="field">
          <label htmlFor="nd-serial">{t('deviceAdmin.serial')}</label>
          <input id="nd-serial" value={serial} required pattern="[A-Za-z0-9][A-Za-z0-9_\-]{2,39}" onChange={(e) => setSerial(e.target.value.trim())} placeholder="DG-0001" />
        </div>
      </div>
      <div className="field">
        <label htmlFor="nd-model">{t('deviceAdmin.model')}</label>
        <select id="nd-model" value={model || models.data?.[0]?.id || ''} onChange={(e) => setModel(e.target.value)}>
          {models.data?.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>
      <p className="faint">{t('deviceAdmin.serialHint')}</p>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="row">
        <button className="btn" disabled={busy || !models.data?.length}>{busy ? t('editor.saving') : t('deviceAdmin.create')}</button>
        <button type="button" className="btn btn--ghost" onClick={onCancel}>{t('connections.cancel')}</button>
      </div>
    </form>
  );
}

export function Devices({ me }: { me: Me }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState<'pair' | 'manual' | null>(null);
  const [created, setCreated] = useState<Created | null>(null);
  const [paired, setPaired] = useState<Paired | null>(null);
  const q = useQuery({ queryKey: ['devices'], queryFn: () => api<DeviceRow[]>('/api/devices'), refetchInterval: 30_000 });
  const canAdd = me.role === 'owner' && !me.demo;
  if (q.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (q.isError) return <p className="error">{t('common.error')}</p>;
  return (
    <section className="stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1>{t('devices.title')}</h1>
        {canAdd && !adding && (
          <div className="row">
            <button className="btn btn--small" onClick={() => (setAdding('pair'), setCreated(null), setPaired(null))}>+ {t('deviceAdmin.pair')}</button>
            <button className="btn btn--small btn--ghost" onClick={() => (setAdding('manual'), setCreated(null), setPaired(null))}>{t('deviceAdmin.addManual')}</button>
          </div>
        )}
      </div>
      {adding === 'pair' && <PairDevice onCancel={() => setAdding(null)} onPaired={async (d) => (setPaired(d), setAdding(null), await qc.invalidateQueries({ queryKey: ['devices'] }))} />}
      {adding === 'manual' && <AddDevice onCancel={() => setAdding(null)} onCreated={async (d) => (setCreated(d), setAdding(null), await qc.invalidateQueries({ queryKey: ['devices'] }))} />}
      {paired && (
        <div className="card stack" role="status">
          <p>{t(paired.repaired ? 'deviceAdmin.repaired' : 'deviceAdmin.paired', { serial: paired.serial })}</p>
          <div className="row">
            <Link href={`/devices/${paired.id}`} className="btn btn--small">{t('devices.open')}: {paired.name}</Link>
            <button className="btn btn--small btn--ghost" onClick={() => setPaired(null)}>{t('deviceAdmin.done')}</button>
          </div>
        </div>
      )}
      {created && (
        <div className="stack">
          <TokenReveal token={created.token} serial={created.serial} onDone={() => setCreated(null)} />
          <Link href={`/devices/${created.id}`} className="btn btn--small btn--ghost" style={{ justifySelf: 'start' }}>{t('devices.open')}: {created.name}</Link>
        </div>
      )}
      {q.data!.length === 0 && !adding && <p className="muted">{t('devices.empty')}</p>}
      <ul className="device-list">
        {q.data!.map((d) => (
          <li key={d.id} className="card stack">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2>{d.name}</h2>
              <span className={`badge badge--${d.status === 'never_connected' ? 'offline' : d.status}`}>{t(`devices.status.${d.status}`)}</span>
            </div>
            <p className="faint">{d.clusterLabel} · {d.serial}</p>
            {d.lastSeenAt && <p className="faint">{t('devices.lastSeen')}: {new Date(d.lastSeenAt).toLocaleString()}</p>}
            {d.firmwareVersion && <p className="faint">{t('devices.firmware')}: {d.firmwareVersion}{d.rssi != null ? ` · ${t('devices.signal')}: ${d.rssi} dBm` : ''}</p>}
            {d.activeSet != null && <p className="faint">{t('health.activeSet')}: {t('configurator.position', { n: d.activeSet + 1 })}</p>}
            {d.sync === 'pending' && <p className="warn">{t('devices.syncPending')}</p>}
            {!!d.errorCount && <p className="error">{t('health.errors', { count: d.errorCount })}</p>}
            <Link href={`/devices/${d.id}`} className="btn btn--small" style={{ justifySelf: 'start' }}>{t('devices.open')}</Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
